import { Card, Grid } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Swal from "sweetalert2";
import { connect } from "react-redux";
import { getSoundGroupsLookups, getSoundLettersLookups } from "redux/actions/pages/sound-material";
import { SOUND_GROUPS, SOUND_LETTERS } from "redux/actions/constants";
import PropTypes from "prop-types";

function CreateSoundMaterial({ getSoundGroups, getSoundLetters, lookups }) {
  const [selectedLetters, setSelectedLetters] = useState([]);
  const [letterGroups, setLetterGroups] = useState([]);
  const history = useHistory();

  useEffect(() => {
    getSoundGroups();
    getSoundLetters();
  }, []);

  useEffect(() => {
    if (lookups && lookups[SOUND_GROUPS]) {
      // Ses gruplarını oluştur
      const groups = lookups[SOUND_GROUPS].lookupValues.map((group) => ({
        id: group.id,
        key: group.lookupValueKey,
        label: `${group.lookupValueKey.replace("SOUND_GROUP_", "")}. Grup`,
        letters: [],
      }));

      setLetterGroups(groups);
    }

    if (lookups && lookups[SOUND_LETTERS]) {
      // Ses harflerini gruplara dağıt
      if (lookups[SOUND_LETTERS].lookupValues && lookups[SOUND_LETTERS].lookupValues.length > 0) {
        const letters = lookups[SOUND_LETTERS].lookupValues;

        // Harfleri gruplara dağıt
        setLetterGroups((prevGroups) =>
          prevGroups.map((group) => {
            // Her gruba ait harfleri filtrele
            const groupLetters = letters
              .filter((letter) => letter.parentValueKey === group.key)
              .map((letter) => ({
                id: letter.id,
                value: letter.lookupValueKey,
                label: letter.lookupValueKey
                  .replace("LETTER_", "")
                  .replace("_WITH_DOT", "i")
                  .replace("_WITH_DIARESIS", "ü")
                  .replace("_WITH_SOFT_SIGN", "ç")
                  .replace("_WITH_CEDILLA", "ş")
                  .replace("_WITH_LINE", "ğ")
                  .replace("_", ""),
                sequence: letter.sequence,
              }));

            return {
              ...group,
              letters: groupLetters.sort((a, b) => a.sequence - b.sequence),
            };
          })
        );
      }
    }
  }, [lookups]);

  const handleLetterClick = (letter) => {
    setSelectedLetters((prev) => {
      const isSelected = prev.some((item) => item.value === letter.value);
      if (isSelected) {
        return prev.filter((item) => item.value !== letter.value);
      }
      return [...prev, letter];
    });
  };

  const handleGroupClick = (group) => {
    const isGroupSelected = group.letters.every((letter) =>
      selectedLetters.some((selected) => selected.value === letter.value)
    );

    if (isGroupSelected) {
      // Eğer gruptaki tüm harfler seçiliyse, hepsini çıkar
      setSelectedLetters((prev) =>
        prev.filter(
          (letter) => !group.letters.some((groupLetter) => groupLetter.value === letter.value)
        )
      );
    } else {
      // Eğer gruptaki tüm harfler seçili değilse, hepsini ekle
      const newLetters = [...selectedLetters];
      group.letters.forEach((letter) => {
        if (!newLetters.some((selected) => selected.value === letter.value)) {
          newLetters.push(letter);
        }
      });
      setSelectedLetters(newLetters);
    }
  };

  const isLetterSelected = (letter) => selectedLetters.some((item) => item.value === letter.value);

  const handleCreateMaterial = (letters) => {
    if (letters.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Önce en az bir harf seçiniz",
        confirmButtonText: "Tamam",
      });
      return;
    }
    history.push({
      pathname: "/sound-materials/create/select-material",
      state: { selectedLetters: letters },
    });
  };

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox mt={3} mb={8}>
          <Card>
            <SuiBox p={3}>
              <SuiTypography variant="h5" fontWeight="bold" mb={3}>
                Ses Grupları
              </SuiTypography>

              <SuiBox
                sx={{
                  border: "2px solid #e0e0e0",
                  overflow: "hidden",
                }}
              >
                {letterGroups.map((group, groupIndex) => (
                  <Grid
                    container
                    key={group.id}
                    sx={{
                      borderBottom:
                        groupIndex < letterGroups.length - 1 ? "2px solid #e0e0e0" : "none",
                      minHeight: "80px",
                    }}
                  >
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{
                        p: 2,
                        borderRight: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <SuiBox
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => handleGroupClick(group)}
                      >
                        <SuiTypography variant="h6" fontWeight="bold">
                          {group.label}
                        </SuiTypography>
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} md={11}>
                      <Grid container sx={{ height: "100%" }}>
                        {group.letters.map((letter) => (
                          <Grid
                            item
                            xs={4}
                            sm={3}
                            md={2}
                            key={letter.id}
                            sx={{
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <SuiBox
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                backgroundColor: isLetterSelected(letter)
                                  ? "rgb(52, 71, 103)"
                                  : "rgba(52, 71, 103, 0.1)",
                                "&:hover": {
                                  backgroundColor: isLetterSelected(letter)
                                    ? "rgb(42, 61, 93)"
                                    : "rgba(52, 71, 103, 0.2)",
                                },
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "12px",
                                borderRadius: "12px",
                              }}
                              onClick={() => handleLetterClick(letter)}
                            >
                              <SuiTypography
                                variant="h6"
                                sx={{
                                  color: isLetterSelected(letter)
                                    ? "rgb(255, 255, 255)"
                                    : "rgb(52, 71, 103)",
                                  fontWeight: isLetterSelected(letter) ? 600 : 400,
                                }}
                              >
                                {letter.label}
                              </SuiTypography>
                            </SuiBox>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </SuiBox>
              <SuiBox mt={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <SuiButton
                  variant="gradient"
                  color="dark"
                  onClick={() => handleCreateMaterial(selectedLetters)}
                >
                  Materyal Oluştur
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </Card>
        </SuiBox>
      </DashboardLayout>
    </>
  );
}

CreateSoundMaterial.propTypes = {
  getSoundGroups: PropTypes.func.isRequired,
  getSoundLetters: PropTypes.func.isRequired,
  lookups: PropTypes.objectOf(PropTypes.any),
};

CreateSoundMaterial.defaultProps = {
  lookups: null,
};

const mapStateToProps = (state) => ({
  lookups: state.data.lookups,
});

const mapDispatchToProps = {
  getSoundGroups: getSoundGroupsLookups,
  getSoundLetters: getSoundLettersLookups,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSoundMaterial);
